/* eslint-disable sonarjs/no-duplicate-string */
import { ReactElement, useCallback, useState, Ref, useRef, useMemo, useContext } from "react";

import { FilledTextFieldProps, OutlinedTextFieldProps, StandardTextFieldProps } from "@mui/material/TextField";
import { DateTimePicker as MaterialDateTimePicker } from "@mui/x-date-pickers-pro";
import { CadIsoLocale, GbIsoLocale, IsoLocale, UsaIsoLocale } from "@simplyk/common";
import { DateTime } from "luxon";
import { FieldValues } from "react-hook-form";

import { useCurrentUserContext } from "../../../contexts/CurrentUserContext";
import { useLocaleContext } from "../../../contexts/LocaleContext";
import { DatePickerLeftArrow, DatePickerRightArrow } from "../DashboardDatePicker/DatePickerArrows";
import { DashboardIconButton } from "../IconButton";
import { DashboardTextField } from "../TextField/DashboardTextField";

import { DateTimePickerTextFieldContext } from "./DateTimePickerTextFieldContext";
import { arrowIconButton, popover } from "./styles";
import { ControlledDateTimePickerProps } from "./types";

import { useCurrentInputVibe } from "@/hooks/useCurrentInputVibe";
import { useFormInputRefs } from "@/hooks/useFormInputRefs";
import { Schedule as ScheduleIcon } from "@/icons/outlined";

const getLuxonFormatFromLocale = (isoLocale: IsoLocale) => {
  if (isoLocale === CadIsoLocale.FR) {
    return "dd/MM/yyyy HH:mm";
  }
  if (isoLocale === CadIsoLocale.EN) {
    return "dd/MM/yyyy hh:mm a";
  }
  if (isoLocale === UsaIsoLocale.EN) {
    return "MM/dd/yyyy hh:mm a";
  }
  if (isoLocale === UsaIsoLocale.ES) {
    return "dd/MM/yyyy hh:mm a";
  }
  if (isoLocale === GbIsoLocale.EN) {
    return "dd/MM/yyyy hh:mm a";
  }
  return "dd/MM/yyyy hh:mm a";
};
export function ControlledDateTimePicker<T extends FieldValues>({
  inputRef,
  className,
  vibe,
  disabled,
  inputProps,
  name,
  size,
  error,
  errorMessage,
  endElement,
  label,
  onChange,
  ref,
  value,
  onBlur,
  minDate,
  maxDate,
  disablePast,
  "data-test": dataTest,
}: ControlledDateTimePickerProps<T>): ReactElement<ControlledDateTimePickerProps<T>> {
  const { isoLocale } = useLocaleContext();
  const { isOrganizationCountryUnitedStates, isOrganizationCountryGreatBritain } = useCurrentUserContext();

  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  const handleChangeDate = useCallback(
    (newDate: DateTime | null) => {
      onChange?.(newDate?.toJSDate() || null);
    },
    [onChange]
  );

  const popperRef = useRef<HTMLInputElement>(null);
  const refCallback = useFormInputRefs({ inputRefCallBack: ref, inputRef, inputRefs: [popperRef] });

  const arrowProps = useMemo(() => ({ sx: arrowIconButton, disableRipple: true, tabIndex: -1 }), []);

  return (
    <DateTimePickerTextFieldContext.Provider
      value={{
        "data-test": dataTest,
        error,
        errorMessage,
        name,
        onOpen,
        refCallback,
        size,
        vibe,
        endElement,
      }}
    >
      <MaterialDateTimePicker
        ref={ref as Ref<HTMLInputElement>}
        slotProps={{
          nextIconButton: arrowProps,
          previousIconButton: arrowProps,
          popper: {
            sx: popover,
            anchorEl: popperRef.current,
          },
          textField: { label, inputProps, className, onBlur },
        }}
        slots={{
          rightArrowIcon: DatePickerRightArrow,
          leftArrowIcon: DatePickerLeftArrow,
          textField: DateTimePickerField,
        }}
        ampm={isOrganizationCountryUnitedStates || isOrganizationCountryGreatBritain}
        disabled={disabled}
        onChange={handleChangeDate}
        onClose={onClose}
        onOpen={onOpen}
        open={open}
        inputRef={refCallback}
        value={value ? DateTime.fromJSDate(value) : null}
        format={getLuxonFormatFromLocale(isoLocale)}
        minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
        maxDate={maxDate ? DateTime.fromJSDate(maxDate) : undefined}
        disablePast={disablePast}
      />
    </DateTimePickerTextFieldContext.Provider>
  );
}

const DateTimePickerField = ({
  className,
  disabled,
  inputProps,
  label,
  onBlur,
  onChange,
  onFocus,
  value,
  placeholder,
  onMouseUp,
  onClick,
}: StandardTextFieldProps | FilledTextFieldProps | OutlinedTextFieldProps) => {
  const {
    "data-test": dataTest,
    error,
    errorMessage,
    name,
    onOpen,
    refCallback,
    size,
    vibe,
    endElement,
  } = useContext(DateTimePickerTextFieldContext);

  const currentInputVibe = useCurrentInputVibe(vibe);

  return (
    <DashboardTextField
      className={className}
      data-test={dataTest}
      errorMessage={errorMessage}
      error={error}
      inputRef={refCallback}
      vibe={currentInputVibe}
      disabled={disabled}
      endAdornment={
        <DashboardIconButton
          inline
          size={size === "small" ? "medium" : "large"}
          vibe={currentInputVibe === "form" ? "inherit" : "neutral"}
          variant="text"
          disabled={disabled}
          onClick={!disabled ? onOpen : undefined}
          data-test={`${dataTest}-button`}
        >
          <ScheduleIcon />
        </DashboardIconButton>
      }
      inputProps={inputProps}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      size={size}
      value={value}
      label={label}
      endElement={endElement}
      placeholder={placeholder}
      onMouseUp={onMouseUp}
      onClick={onClick}
    />
  );
};
